/**
 * Custom hook for filtering events
 * Handles search query, event type filtering, theme filtering and pagination
 * 
 * Similar to use-working-groups-filter but adapted for events
 */

'use client';

import { useEffect, useMemo, useState } from 'react';
import type { EventTeaser } from '../types';

const STRIP_HTML_TAGS = /<[^>]*>/g;

const stripHtml = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  return value.replace(STRIP_HTML_TAGS, '');
};

// Helper to normalize eventType to array (handles both single object and array)
const normalizeEventType = (eventType: EventTeaser['eventType']): Array<NonNullable<NonNullable<EventTeaser['eventType']>>> => {
  if (!eventType) return [];
  return Array.isArray(eventType) ? eventType : [eventType];
};

interface UseEventsFilterOptions {
  events: EventTeaser[];
  pageSize?: number;
}

export function useEventsFilter({
  events,
  pageSize = 12,
}: UseEventsFilterOptions) {
  const safePageSize = Math.max(1, pageSize);

  // Filter state
  const [query, setQuery] = useState('');
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [themeFilterMode, setThemeFilterMode] = useState<'OR' | 'AND'>('OR');
  const [showPastEvents, setShowPastEvents] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const normalizedQuery = query.trim().toLowerCase();

  // Extract unique event types from events
  const eventTypes = useMemo(() => {
    const typeMap = new Map<string, { id: string; name: string; colour?: string | null }>();

    events.forEach((event) => {
      const normalizedTypes = normalizeEventType(event.eventType);
      normalizedTypes.forEach((type) => {
        if (type?.id && type?.name && !typeMap.has(type.id)) {
          // Type narrowing to access eventTypeColor
          const colour = type.__typename === 'TermEventType' && 'eventTypeColor' in type
            ? type.eventTypeColor
            : null;
          
          typeMap.set(type.id, {
            id: type.id,
            name: type.name,
            colour,
          });
        }
      });
    });

    return Array.from(typeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  // Extract unique themes from events
  const themes = useMemo(() => {
    const themeMap = new Map<string, { id: string; name: string }>();

    events.forEach((event) => {
      const eventThemes = event.theme ?? [];
      eventThemes.forEach((theme) => {
        if (theme?.id && theme?.name && !themeMap.has(theme.id)) {
          themeMap.set(theme.id, {
            id: theme.id,
            name: theme.name,
          });
        }
      });
    });

    return Array.from(themeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  // Filter events
  const filteredEvents = useMemo(() => {
    const now = new Date();
    
    const getEventThemeIds = (event: EventTeaser) =>
      (event.theme ?? []).map((theme) => theme?.id).filter((id): id is string => Boolean(id));

    return events.filter((event) => {
      // Past events filter
      if (!showPastEvents && event.eventDateTimeRange?.end?.timestamp) {
        const eventEnd = new Date(event.eventDateTimeRange.end.timestamp * 1000);
        if (eventEnd < now) return false;
      }

      // Event type filter - OR logic (at least one of the selected types)
      if (selectedEventTypes.length > 0) {
        const normalizedTypes = normalizeEventType(event.eventType);
        const eventTypeIds = normalizedTypes.map(t => t?.id).filter(Boolean);
        const hasSelectedType = selectedEventTypes.some(typeId => eventTypeIds.includes(typeId));
        if (!hasSelectedType) return false;
      }

      // Theme filter - OR (at least one) or AND (all themes)
      if (selectedThemes.length > 0) {
        const eventThemeIds = getEventThemeIds(event);

        if (themeFilterMode === 'OR') {
          const hasAnyTheme = selectedThemes.some((themeId) => eventThemeIds.includes(themeId));
          if (!hasAnyTheme) return false;
        } else {
          const hasAllThemes = selectedThemes.every((themeId) => eventThemeIds.includes(themeId));
          if (!hasAllThemes) return false;
        }
      }

      // Search query
      if (!normalizedQuery) return true;

      // Search in title
      if (event.title.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in location
      if (event.eventLocation?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in themes
      if (event.theme?.some((theme) => theme?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      // Search in event types
      const normalizedTypes = normalizeEventType(event.eventType);
      if (normalizedTypes.some((type) => type?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      // Search in contact name
      if (event.eventContactName?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      if (event.eventContact?.name?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in body content (strip HTML)
      const bodyText = stripHtml(event.body?.processed ?? event.body?.summary);
      if (bodyText.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return false;
    });
  }, [events, normalizedQuery, selectedEventTypes, selectedThemes, themeFilterMode, showPastEvents]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / safePageSize));

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedQuery, selectedEventTypes, selectedThemes, themeFilterMode, showPastEvents, safePageSize, events]);

  // Ensure current page doesn&apos;t exceed total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Paginated events
  const { visibleEvents, startIndex, endIndex } = useMemo(() => {
    const start = (currentPage - 1) * safePageSize;
    const end = Math.min(start + safePageSize, filteredEvents.length);
    return {
      visibleEvents: filteredEvents.slice(start, end),
      startIndex: start,
      endIndex: end,
    };
  }, [filteredEvents, currentPage, safePageSize]);

  // Actions
  const toggleEventType = (typeId: string) => {
    setSelectedEventTypes((prev) =>
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId]
    );
  };

  const toggleTheme = (themeId: string) => {
    setSelectedThemes((prev) =>
      prev.includes(themeId) ? prev.filter((id) => id !== themeId) : [...prev, themeId]
    );
  };

  const clearEventTypeSelection = () => {
    setSelectedEventTypes([]);
  };

  const clearThemeSelection = () => {
    setSelectedThemes([]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearAllFilters = () => {
    setQuery('');
    setSelectedEventTypes([]);
    setSelectedThemes([]);
    setShowPastEvents(true);
    setCurrentPage(1);
  };

  const totalResults = filteredEvents.length;
  const hasResults = totalResults > 0 || normalizedQuery !== '' || selectedEventTypes.length > 0 || selectedThemes.length > 0;
  const hasActiveFilters =
    normalizedQuery !== '' ||
    selectedEventTypes.length > 0 ||
    selectedThemes.length > 0 ||
    !showPastEvents;

  return {
    query,
    selectedEventTypes,
    selectedThemes,
    themeFilterMode,
    showPastEvents,
    currentPage,
    eventTypes,
    themes,
    visibleEvents,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    setQuery,
    toggleEventType,
    toggleTheme,
    clearEventTypeSelection,
    clearThemeSelection,
    setThemeFilterMode,
    setShowPastEvents,
    handlePageChange,
    clearAllFilters,
  };
}

